'use client';

import { createStore } from 'zustand';

import { getLogger } from '@/logger/logger';
import { addCartItems } from '@/services/cart/add-cart-items.service';
import { clearCart } from '@/services/cart/clear-cart.service';
import { getActiveCart } from '@/services/cart/get-active-cart.service';
import { Cart, CartItemBody, CartPurchaseTrack } from '@/types/cart.types';
import { CartAddIntent, CartClearReason } from '@/types/cart-clear-confirmation.types';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { buildPurchaseTrack } from '@/utils/cart/build-purchase-track.util';
import { trackCartOpened } from '@/utils/cart/cart-analytics.util';

const logger = getLogger('cart-store');

export type CartFetchStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type CartState = {
  isOpen: boolean;
  cart: Cart | null;
  status: CartFetchStatus;
  origin: CartOrigin;
};

const initialState: CartState = {
  isOpen: false,
  cart: null,
  status: 'idle',
  origin: CartOrigin.OTHER,
};

export const cartStore = createStore<CartState>(() => ({ ...initialState }));

let pendingLoad: Promise<void> | null = null;
let pendingLoadAbort: AbortController | null = null;
let pendingLoadIsDrawerOwned = false;

const abortPendingLoad = () => {
  if (!pendingLoad) {
    return;
  }

  pendingLoadAbort?.abort();
  pendingLoad = null;
  pendingLoadAbort = null;
  pendingLoadIsDrawerOwned = false;

  const { status, cart } = cartStore.getState();

  if (status === 'loading') {
    cartStore.setState({ status: cart ? 'loaded' : 'idle' });
  }
};

const abortDrawerLoad = () => {
  if (!pendingLoadIsDrawerOwned) {
    return;
  }

  abortPendingLoad();
};

const startLoad = (isDrawerOwned: boolean): Promise<void> => {
  if (pendingLoad) {
    return pendingLoad;
  }

  const abortController = new AbortController();
  const { signal } = abortController;

  pendingLoadAbort = abortController;
  pendingLoadIsDrawerOwned = isDrawerOwned;

  if (cartStore.getState().status !== 'loaded') {
    cartStore.setState({ status: 'loading' });
  }

  const load = getActiveCart(signal)
    .then((cart) => {
      if (signal.aborted) {
        return;
      }

      cartStore.setState({ cart, status: 'loaded' });

      if (cartStore.getState().isOpen) {
        trackCartOpened(cartStore.getState().origin, cart?.items ?? []);
      }
    })
    .catch((error) => {
      if (signal.aborted) {
        return;
      }

      logger.error('Failed to fetch the active cart', error);
      cartStore.setState({ status: 'error' });
    })
    .finally(() => {
      if (pendingLoad === load) {
        pendingLoad = null;
        pendingLoadAbort = null;
        pendingLoadIsDrawerOwned = false;
      }
    });

  pendingLoad = load;

  return load;
};

export const loadCart = (): Promise<void> => startLoad(false);

export interface CartReadResult {
  cart: Cart | null;
  wasRead: boolean;
}

export const ensureCartLoaded = async (): Promise<CartReadResult> => {
  if (cartStore.getState().status !== 'loaded') {
    await loadCart();
  }

  const { cart, status } = cartStore.getState();

  return { cart, wasRead: status === 'loaded' };
};

export const openCart = (origin: CartOrigin) => {
  cartStore.setState({ isOpen: true, origin });

  void startLoad(true);
};

export const retryCart = (): Promise<void> => {
  cartStore.setState({ isOpen: true });

  return startLoad(true);
};

export const MIXED_CART_BATCH_ERROR_MESSAGE
  = 'A standalone guide cannot be published together with scheduled items: send one batch for each.';

const isMixedBatch = (items: CartItemBody[]): boolean =>
  items.some((item) => !item.slotId) && items.some((item) => Boolean(item.slotId));

const hasStandaloneItem = (cart: Cart | null): boolean =>
  cart?.items.some((item) => !item.slot.id) ?? false;

export const getCartClearReason = (cart: Cart | null, intent: CartAddIntent): CartClearReason | null => {
  const cartHasItems = (cart?.items.length ?? 0) > 0;

  if (intent.isStandalone && cartHasItems) {
    return CartClearReason.ADDING_STANDALONE;
  }

  if (hasStandaloneItem(cart)) {
    return CartClearReason.CART_HAS_STANDALONE;
  }

  return null;
};

export const toCartAddIntent = (items: CartItemBody[]): CartAddIntent => ({
  isStandalone: items.some((item) => !item.slotId),
});

const shouldClearBeforeAdding = (cart: Cart | null, items: CartItemBody[]): boolean =>
  getCartClearReason(cart, toCartAddIntent(items)) !== null;

export interface AddItemsToCartResult {
  cart: Cart | null;
  purchaseTrack?: CartPurchaseTrack;
}

export const addItemsToCart = async (
  items: CartItemBody[],
  origin: CartOrigin,
): Promise<AddItemsToCartResult> => {
  if (isMixedBatch(items)) {
    throw new Error(MIXED_CART_BATCH_ERROR_MESSAGE);
  }

  await ensureCartLoaded();

  const purchaseTrack = await buildPurchaseTrack();

  if (shouldClearBeforeAdding(cartStore.getState().cart, items)) {
    await clearCart();
  }

  abortPendingLoad();

  const cart = await addCartItems({ items, purchaseTrack });

  cartStore.setState({ cart, status: 'loaded', isOpen: true, origin });
  trackCartOpened(origin, cart?.items ?? []);

  return { cart, purchaseTrack };
};

export const closeCart = () => {
  abortDrawerLoad();
  cartStore.setState({ isOpen: false });
};

export const setCartOpen = (isOpen: boolean, origin: CartOrigin = CartOrigin.OTHER) => {
  if (isOpen) {
    openCart(origin);
    return;
  }

  closeCart();
};

export const resetCart = () => {
  abortPendingLoad();
  cartStore.setState({ ...initialState });
};
